import React, { useState } from 'react';
import { Mail, X, Send, Paperclip, Smartphone, MessageSquare } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const MessageModal = ({ recipientName, recipientEmail, recipientPhone, onClose }) => {
  const [messageType, setMessageType] = useState('email');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const messageTypes = [
    { id: 'email', label: 'Email', icon: Mail, color: 'blue' },
    { id: 'sms', label: 'SMS', icon: Smartphone, color: 'green' },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'emerald' }
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (!message?.trim()) {
      alert('Please enter a message');
      return;
    }

    if (messageType === 'email' && !subject?.trim()) {
      alert('Please enter a subject for the email');
      return;
    }

    setIsSubmitting(true);
    console.log('Sending message:', {
      messageType,
      subject,
      message,
      attachments,
      recipient: {
        name: recipientName,
        email: recipientEmail,
        phone: recipientPhone
      }
    });

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onClose();
      // Show success message
    }, 1500);
  };

  const getRecipientInfo = () => {
    switch (messageType) {
      case 'email':
        return recipientEmail;
      case 'sms': case'whatsapp':
        return recipientPhone;
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Send Message</h3>
              <p className="text-sm text-gray-600">To: {recipientName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Message Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Communication Channel
            </label>
            <div className="grid grid-cols-3 gap-3">
              {messageTypes?.map((type) => {
                const Icon = type?.icon;
                return (
                  <button
                    key={type?.id}
                    onClick={() => setMessageType(type?.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      messageType === type?.id
                        ? `border-${type?.color}-500 bg-${type?.color}-50`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 mx-auto mb-2 ${
                        messageType === type?.id
                          ? `text-${type?.color}-600`
                          : 'text-gray-400'
                      }`}
                    />
                    <p
                      className={`text-sm font-medium ${
                        messageType === type?.id
                          ? `text-${type?.color}-900`
                          : 'text-gray-700'
                      }`}
                    >
                      {type?.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipient Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Sending to: <span className="font-medium text-gray-900">{getRecipientInfo()}</span>
            </p>
          </div>

          {/* Subject (Email Only) */}
          {messageType === 'email' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e?.target?.value)}
                placeholder="Enter email subject..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Message */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder={`Enter your ${messageType} message...`}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="6"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{message?.length} characters</span>
              {messageType === 'sms' && message?.length > 160 && (
                <span className="text-yellow-600">
                  Multiple SMS messages will be sent
                </span>
              )}
            </div>
          </div>

          {/* Attachments (Email Only) */}
          {messageType === 'email' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2"
                >
                  <Paperclip className="w-5 h-5" />
                  Choose Files
                </label>
              </div>

              {/* Attachment List */}
              {attachments?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments?.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    >
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {file?.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-red-600 hover:text-red-700 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2.5 rounded-lg font-medium disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isSubmitting || !message?.trim() || (messageType === 'email' && !subject?.trim())}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageModal;